import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../../../services/external/APIs/APIErrorHandler";
import {
  createWorkshiftSchedulings,
  deleteWorkshiftSchedulings,
  getOvertimeSchedule,
} from "../api/overtimeScheduleApi";
import { OvertimeWorkedAndScheduledPerPeriodRequest } from "../models/OvertimeSchedule";
import {
  CreateWorkshiftSchedulingRequest,
  DeleteWorkshiftsSchedulingRequest,
} from "../models/WorkingSchedulingRequests";

const shouldRetryOvertimeSchedule = (
  failureCount: number,
  error: unknown,
): boolean => {
  const status = error instanceof ApiError ? error.status : undefined;

  if (status && status >= 400 && status < 500) {
    return false;
  }

  return failureCount < 1;
};

export const useOvertimeSchedule = (
  model: OvertimeWorkedAndScheduledPerPeriodRequest,
) => {
  return useQuery({
    queryKey: ["overtime-schedule", model],
    queryFn: () => getOvertimeSchedule(model),
    enabled: model.employeeIds.length > 0,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: shouldRetryOvertimeSchedule,
  });
};

export const useSaveWorkshiftSchedulingMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateWorkshiftSchedulingRequest) =>
      createWorkshiftSchedulings(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["overtime-schedule"] });
    },
  });
};

export const useDeleteWorkshiftSchedulingMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: DeleteWorkshiftsSchedulingRequest) =>
      deleteWorkshiftSchedulings(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["overtime-schedule"] });
    },
  });
};
